import {
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  increment,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore";

import { db } from "./firebase";

const QUERY_IN_LIMIT = 10;

function chunk<T>(items: T[], size: number) {
  const result: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    result.push(items.slice(index, index + size));
  }
  return result;
}

/** Loads only the authenticated user's like documents, never a post's full liker list. */
export async function getMyLikedPostIds(postIds: string[], uid: string) {
  const ids = Array.from(new Set(postIds.filter(Boolean)));
  const liked = new Set<string>();
  if (!uid || !ids.length) return liked;

  await Promise.all(
    chunk(ids, QUERY_IN_LIMIT).map(async (part) => {
      const snap = await getDocs(
        query(
          collectionGroup(db, "likes"),
          where("uid", "==", uid),
          where("postId", "in", part)
        )
      );
      snap.docs.forEach((likeDoc) => {
        const postId = likeDoc.data()?.postId;
        if (typeof postId === "string" && postId) liked.add(postId);
      });
    })
  );

  return liked;
}

/**
 * Writes a like marker and its counter update atomically. The marker is the
 * source of truth; likedBy remains only as a legacy field during migration.
 */
export async function togglePostLike(params: {
  postId: string;
  uid: string;
  currentlyLiked: boolean;
}) {
  const likeRef = doc(db, "posts", params.postId, "likes", params.uid);
  const postRef = doc(db, "posts", params.postId);

  if (params.currentlyLiked) {
    const likeSnap = await getDoc(likeRef);
    const batch = writeBatch(db);
    if (likeSnap.exists()) batch.delete(likeRef);
    batch.update(postRef, { likesCount: increment(-1) });
    await batch.commit();
    return false;
  }

  const batch = writeBatch(db);
  batch.set(likeRef, {
    uid: params.uid,
    postId: params.postId,
    createdAt: serverTimestamp(),
  });
  batch.update(postRef, { likesCount: increment(1) });
  await batch.commit();
  return true;
}
